import React, { useState, useContext, useMemo, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { PredContext } from './sportsc/predContext';
import PlayerPage from './playerPage';
// --- STYLED COMPONENTS ---

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #0e141b;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  display: inline-block;
  margin-left: 8px;
  vertical-align: middle;
`;

const StatWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 15px;
`;

const BackBtn = styled.button`
  background: #e2e8f0;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 10px;
`;

const GemToggleBtn = styled.button`
  background: #0e141b;
  color: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 6px 14px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 700;
  text-transform: capitalize;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
`;

const FeatureContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin: 12px 0;
  padding: 12px;
  background: #f1f5f9;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  padding: 6px 8px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const FeatureLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
`;

const FeatureValue = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
`;

const DisplayDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin: 12px 0;
`;

const ChartWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 50vh;
  box-sizing: border-box;
  overflow: hidden; /* Prevents child canvas elements from forcing page overflow */
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const ActionButton = styled.button.attrs({ type: 'button' })`
  padding: 8px 14px;
  font-size: 0.825rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClearButton = styled(ActionButton)`
  background-color: #ef4444;
  color: white;
`;

const SendButton = styled(ActionButton)`
  background-color: #16a34a;
  color: white;
`;

const SecondaryButton = styled(ActionButton)`
  background-color: #64748b;
  color: white;
`;

const LinesContainer = styled.div`
  background: white;
  margin: 0 1rem 1rem;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 450px;
  overflow-y: auto;
`;

const LinesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.875rem;

  th {
    padding: 8px;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    background: white;
    user-select: none;

    &.filterable {
      cursor: pointer;

      &:hover {
        color: #0f172a;
        background-color: #f8fafc;
      }
    }
  }

  td {
    padding: 10px 8px;
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
    font-weight: 500;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ClickableCell = styled.td`
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #1d4ed8;
  }
`;

const ActiveFilterTag = styled.span`
  margin-left: 4px;
  padding: 2px 5px;
  background-color: #2563eb;
  color: white;
  border-radius: 4px;
  font-size: 0.65rem;
  text-transform: none;
`;

const Badge = styled.span`
  background: #e2e8f0;
  color: #0e141b;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 6px;
  text-transform: uppercase;
`;

const PickBadge = styled(Badge)`
  background: ${(props) => (props.$pick === 'OVER' ? '#dcfce7' : '#fee2e2')};
  color: ${(props) => (props.$pick === 'OVER' ? '#15803d' : '#b91c1c')};
`;

const EmptyText = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
  margin: 1rem 0;
`;

// --- HELPER FUNCTIONS ---

function shortenValue(val) {
  if (typeof val === 'string' && isNaN(val)) return val;
  return Number(val).toFixed(2);
}

// --- PLAYER PAGE COMPONENT ---



// --- LINES VIEW COMPONENT ---

function LinesView({ onSendLines }) {
  const { lines, clearLines } = useContext(PredContext);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Selected player for PlayerPage view
  const [selectedPlayerObj, setSelectedPlayerObj] = useState(null);

  // Multi-column filter state
  const [filters, setFilters] = useState({});

  // Cycle through unique values for a column key safely
  const handleCycleFilter = (key, dataset, getValueFn) => {
    if (!Array.isArray(dataset) || dataset.length === 0) return;

    const uniqueValues = Array.from(
      new Set(dataset.map(getValueFn).filter((v) => v !== undefined && v !== null && v !== ''))
    );

    if (uniqueValues.length === 0) return;

    setFilters((prev) => {
      const currentValue = prev[key];

      if (currentValue === undefined) {
        return { ...prev, [key]: uniqueValues[0] };
      }

      const currentIndex = uniqueValues.indexOf(currentValue);
      if (currentIndex !== -1 && currentIndex < uniqueValues.length - 1) {
        return { ...prev, [key]: uniqueValues[currentIndex + 1] };
      }

      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  // Flatten input lines safely
  const formattedLines = useMemo(() => {
    if (!lines || typeof lines !== 'object') return [];

    const result = [];
    Object.entries(lines).forEach(([statType, playersMap]) => {
      if (!playersMap || typeof playersMap !== 'object') return;
      Object.entries(playersMap).forEach(([player, data]) => {
        if (!data) return;
        result.push({
          player,
          type: statType,
          line: data.line ?? 'N/A',
          team: data.team ? String(data.team).replace(/\.csv$/, '') : 'N/A',
        });
      });
    });

    return result;
  }, [lines]);

  // Filtered Input Lines
  const filteredInputLines = useMemo(() => {
    if (!Array.isArray(formattedLines)) return [];
    const activeKeys = Object.keys(filters);
    if (activeKeys.length === 0) return formattedLines;

    return formattedLines.filter((item) =>
      activeKeys.every((key) => String(item[key]) === String(filters[key]))
    );
  }, [formattedLines, filters]);

  // Column index mapping for API results
  const resultColumnMap = {
    player: 0,
    metric: 1,
    model: 2,
    pred: 3,
    line: 4,
    edge: 5,
    pick: 7,
    winProb: 8,
  };

  // Filtered Results safely guarded against non-array structures
  const filteredResults = useMemo(() => {
    if (!Array.isArray(results)) return [];
    const activeKeys = Object.keys(filters);
    if (activeKeys.length === 0) return results;

    return results.filter((row) => {
      if (!Array.isArray(row)) return false;
      return activeKeys.every((key) => {
        const targetIdx = resultColumnMap[key];
        if (targetIdx === undefined) return true;
        return String(row[targetIdx] ?? '') === String(filters[key] ?? '');
      });
    });
  }, [results, filters]);

  // Direct API fetch helper
  const fetchLinesApi = async (payload) => {
    const res = await axios({
      method: 'post',
      url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
      withCredentials: false,
      data: {
        option: 'lines',
        players: payload,
      },
    });

    return res.data?.data;
  };

  // Unified Async Send Handler
  const handleSend = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      let data = null;

      if (typeof onSendLines === 'function') {
        const customResult = await onSendLines(formattedLines);
        if (Array.isArray(customResult)) {
          data = customResult;
        }
      }

      if (!data) {
        data = await fetchLinesApi(formattedLines);
      }

      if (Array.isArray(data)) {
        setResults(data);
        setFilters({});
      } else {
        setError('Invalid response payload format from server.');
      }
    } catch (err) {
      console.error('Error fetching lines:', err);
      setError('Failed to fetch predictions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = (e) => {
    if (e) e.preventDefault();
    setResults(null);
    setFilters({});
    clearLines();
  };

  const renderFilterIndicator = (key) => {
    if (filters[key]) {
      return <ActiveFilterTag>{filters[key]}</ActiveFilterTag>;
    }
    return null;
  };

  // 0. Render Player Detail View if Selected
  if (selectedPlayerObj) {
    return (
      <div>
      <PlayerPage
        player={selectedPlayerObj.player}
        view={selectedPlayerObj.view}
        team={selectedPlayerObj.team}
        onBack={() => setSelectedPlayerObj(null)}
      />
      </div>
    );
  }

  // 1. Loading State
  if (loading) {
    return (
      <LinesContainer>
        <EmptyText>Fetching predictions...</EmptyText>
      </LinesContainer>
    );
  }

  // 2. Render Results View
  if (Array.isArray(results)) {
    return (
      <LinesContainer>
        {error && <EmptyText style={{ color: '#ef4444' }}>{error}</EmptyText>}
        <LinesTable>
          <thead>
            <tr>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('player', results, (r) => r[resultColumnMap.player])
                }
              >
                Player{renderFilterIndicator('player')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('metric', results, (r) => r[resultColumnMap.metric])
                }
              >
                Metric{renderFilterIndicator('metric')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('model', results, (r) => r[resultColumnMap.model])
                }
              >
                Model{renderFilterIndicator('model')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('pred', results, (r) => r[resultColumnMap.pred])
                }
              >
                Pred{renderFilterIndicator('pred')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('line', results, (r) => r[resultColumnMap.line])
                }
              >
                Line{renderFilterIndicator('line')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('edge', results, (r) => r[resultColumnMap.edge])
                }
              >
                Edge{renderFilterIndicator('edge')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('pick', results, (r) => r[resultColumnMap.pick])
                }
              >
                Pick{renderFilterIndicator('pick')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('winProb', results, (r) => r[resultColumnMap.winProb])
                }
              >
                Win %{renderFilterIndicator('winProb')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((row, idx) => {
              if (!Array.isArray(row)) return null;

              const [
                player = 'N/A',
                 metric = 'N/A',
                model = 'N/A',
                prediction = 0,
                line = 0,
                edge = 0,
                rmse = 0,
                pick = 'N/A',
                winProb = 0,
                team=''
              ] = row;

              const numPred = Number(prediction);
              const numEdge = Number(edge);
              const numWinProb = Number(winProb);

              return (
                <tr key={`${player}-${model}-${idx}`}>
                  <ClickableCell
                    onClick={() =>
                      setSelectedPlayerObj({
                        player,
                        view: metric.toLowerCase(),
                        team: team,
                      })
                    }
                  >
                    {player}
                  </ClickableCell>
                  <td>
                    <Badge>{metric}</Badge>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{model}</td>
                  <td>{!isNaN(numPred) ? numPred.toFixed(2) : 'N/A'}</td>
                  <td style={{ fontWeight: 700 }}>{line}</td>
                  <td
                    style={{
                      color: !isNaN(numEdge) && numEdge > 0 ? '#16a34a' : '#dc2626',
                      fontWeight: 600,
                    }}
                  >
                    {!isNaN(numEdge) ? (numEdge > 0 ? `+${numEdge}` : numEdge) : 'N/A'}
                  </td>
                  <td>
                    <PickBadge $pick={pick}>{pick}</PickBadge>
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    {!isNaN(numWinProb) ? `${(numWinProb * 100).toFixed(1)}%` : 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </LinesTable>

        <ActionRow>
          <SecondaryButton
            onClick={(e) => {
              if (e) e.preventDefault();
              setResults(null);
              setFilters({});
            }}
          >
            Back to Input
          </SecondaryButton>
          <ClearButton onClick={handleClearAll}>Clear All</ClearButton>
        </ActionRow>
      </LinesContainer>
    );
  }

  // 3. Render Empty State
  if (formattedLines.length === 0) {
    return (
      <LinesContainer>
        <EmptyText>No active lines submitted yet.</EmptyText>
      </LinesContainer>
    );
  }

  // 4. Default Input Lines View
  return (
    <LinesContainer>
      {error && <EmptyText style={{ color: '#ef4444' }}>{error}</EmptyText>}
      <LinesTable>
        <thead>
          <tr>
            <th
              className="filterable"
              onClick={() => handleCycleFilter('player', formattedLines, (i) => i.player)}
            >
              Player{renderFilterIndicator('player')}
            </th>
            <th
              className="filterable"
              onClick={() => handleCycleFilter('type', formattedLines, (i) => i.type)}
            >
              Type{renderFilterIndicator('type')}
            </th>
            <th
              className="filterable"
              onClick={() => handleCycleFilter('line', formattedLines, (i) => i.line)}
            >
              Line{renderFilterIndicator('line')}
            </th>
            <th
              className="filterable"
              onClick={() => handleCycleFilter('team', formattedLines, (i) => i.team)}
            >
              Team{renderFilterIndicator('team')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredInputLines.map((item, idx) => (
            <tr key={`${item.player}-${item.type}-${idx}`}>
              <ClickableCell
                onClick={() =>
                  setSelectedPlayerObj({
                    player: item.player,
                    view: item.type.toLowerCase(),
                    team: item.team,
                  })
                }
              >
                {item.player}
              </ClickableCell>
              <td>
                <Badge>{item.type}</Badge>
              </td>
              <td style={{ fontWeight: 700, color: '#16a34a' }}>{item.line}</td>
              <td>{item.team}</td>
            </tr>
          ))}
        </tbody>
      </LinesTable>

      <ActionRow>
        <ClearButton
          onClick={(e) => {
            if (e) e.preventDefault();
            clearLines();
          }}
        >
          Clear Lines
        </ClearButton>
        <SendButton onClick={handleSend} disabled={loading}>
          Send Lines
        </SendButton>
      </ActionRow>
    </LinesContainer>
  );
}

export default LinesView;